import React, { useEffect, useState } from "react";
import FormSectionHeading from "../../components/Form/FormSectionHeading";
import ProfileImageUploader from "../../components/Form/ProfileImageUploader";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import useForm from "../../hooks/useForm";
import CustomInput from "../../components/Form/Input";
import ImageUpload from "../../components/Form/ImageUpload";
import {
  firebaseUplaodFolders,
  maximumImagesUploadingSizes,
  mobileMoneyProviders,
  paymentTypes,
  referencesFormats,
} from "../../constants/commonConstants";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Dropdown from "../../components/Form/Dropdown";
import ProfessionalDetailsSection from "../../components/Tasker/ProfessionalDetailsSection";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/Form/CustomButton";
import ServingAreasSection from "../../components/Tasker/ServingAreasSection";
import { useThunk } from "../../hooks/useThunk";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import {
  fetchBanks,
  taskerSignup,
  verifyBankAccount,
  verifyMobileMoney,
} from "../../store";
import { useSelector } from "react-redux";
import showToast from "../../utils/toastNotifications";
import convertUploadedFile from "../../utils/convertUploadedFile";
import { errorMessages } from "../../constants/frontendErrorMessages";
import ActionLoader from "../../components/Common/ActionLoader";

const TaskerSignUp = () => {
  const navigate = useNavigate();

  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const { ghanaBanks } = useSelector((state) => state.payStack);

  // NOTE: custom hook to dispatch the action
  const [doSignUpAsTasker, isSignUpAsTasker, signUpAsTaskerError] =
    useThunk(taskerSignup);

  // NOTE: Retrieves a list of supported banks in Ghana
  const [doFetchBanks, isFetchBanksLoading, fetchBanksError] =
    useThunk(fetchBanks);

  // NOTE: Verify bank account number
  const [
    doVerifyBankAccount,
    isVerifyBankAccountLoading,
    verifyBankAccountError,
  ] = useThunk(verifyBankAccount);

  // NOTE: Verify mobile money
  const [
    doVerifyMobileMoney,
    isVerifyMobileMoneyLoading,
    verifyMobileMoneyError,
  ] = useThunk(verifyMobileMoney);

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  // Payment Type State
  const [paymentType, setPaymentType] = useState(null);
  const [paymentTypeError, setPaymentTypeError] = useState(false);
  const [bankCode, setBankCode] = useState(null);
  const [mobileMoneyProviderCode, setMobileMoneyProviderCode] = useState(null);

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState("");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
    setFormData,
  } = useForm(
    {
      // Personal Details
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      email: user?.email ?? "",
      phoneNumber: user?.phoneNumber ?? "",
      idNumber: "",
      aboutMe: "",
      // Password
      password: "",
      confirmPassword: "",
      // Payout Details - Mobile Money
      mobileMoneyProvider: "",
      mobileNumber: "",
      mobileMoneyAccHolderFullName: "",
      // Payout Details - Bank Details
      bankName: "",
      bankAccHolderFullName: "",
      accNumber: "",
      branchName: "",
    },
    {
      // Personal Details
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      idNumber: true,
      aboutMe: false,
      // Password
      password: !isAuthenticated ? true : false,
      confirmPassword: !isAuthenticated ? true : false,
      // Payout Details - Mobile Money
      mobileMoneyProvider:
        paymentType === paymentTypes.MOBILE_MONEY ? true : false,
      mobileNumber: paymentType === paymentTypes.MOBILE_MONEY ? true : false,
      mobileMoneyAccHolderFullName:
        paymentType === paymentTypes.MOBILE_MONEY ? true : false,
      // Payout Details - Bank Details
      bankName: paymentType === paymentTypes.BANK ? true : false,
      bankAccHolderFullName: paymentType === paymentTypes.BANK ? true : false,
      accNumber: paymentType === paymentTypes.BANK ? true : false,
      branchName: paymentType === paymentTypes.BANK ? false : false,
    },
    {
      // Personal Details
      firstName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      lastName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      phoneNumber: COMMON_FIELD_TYPES.PHONE,
      idNumber: COMMON_FIELD_TYPES.ID_NUMBER,
      aboutMe: COMMON_FIELD_TYPES.TEXT_FIELD,
      // Password
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
      // Payout Details - Mobile Money
      mobileMoneyProvider: {
        type: COMMON_FIELD_TYPES.DROPDOWN,
        displayKey: "name",
      },
      mobileNumber: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      mobileMoneyAccHolderFullName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      // Payout Details - Bank Details
      bankName: {
        type: COMMON_FIELD_TYPES.DROPDOWN,
        displayKey: "name",
      },
      bankAccHolderFullName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      accNumber: COMMON_FIELD_TYPES.NUMBER_FIELD,
      branchName: COMMON_FIELD_TYPES.TEXT_FIELD,
    }
  );

  // STATE: Professional Details status
  const [professionalDetails, setProfessionalDetails] = useState([
    {
      category: null,
      categoryName: "",
      skills: [
        {
          name: "",
          description: "",
        },
      ],
    },
  ]);
  const [professionalDetailsErrors, setProfessionalDetailsErrors] = useState([
    {
      category: "",
      categoryName: "",
      skills: [
        {
          name: "",
          description: "",
        },
      ],
    },
  ]);
  const [professionalDetailsTouched, setProfessionalDetailsTouched] = useState([
    {
      category: false,
      categoryName: false,
      skills: [
        {
          name: false,
          description: false,
        },
      ],
    },
  ]);
  // STATE: Serving Areas
  const [servingAreas, setServingAreas] = useState([
    {
      city: null,
      cityName: "",
      subcities: [],
    },
  ]);
  const [servingAreasErrors, setServingAreasErrors] = useState([
    {
      city: null,
      cityName: "",
      subcities: "",
    },
  ]);
  const [servingAreasTouched, setservingAreasTouched] = useState([
    {
      city: false,
      cityName: false,
      subcities: false,
    },
  ]);

  // STATE: Password Visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  //only for testing
  // const convertFiles = [
  //   "https://us.123rf.com/450wm/alenanv/alenanv2309/alenanv230900213/214982436-id-card-personal-info-data-identification-document-with-person-photo-user-or-profile-card-driver.jpg?ver=6",
  //   "https://static.vecteezy.com/system/resources/previews/029/460/362/non_2x/id-card-personal-info-data-identification-document-with-person-photo-user-or-profile-card-driver-s-license-flat-style-illustration-vector.jpg",
  // ].map((file) => {
  //   const conFile = convertUploadedFile(file);
  //   return conFile;
  // });

  // STATE: Upload Files
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadFilesErrors, setUploadFilesErrors] = useState(false);
  const [proofAddress, setProofAddress] = useState([]);
  const [proofAddressErrors, setProofAddressErrors] = useState(false);

  // Fetch Banks
  useEffect(() => {
    const fetchBanks = async () => {
      const result = await doFetchBanks();
      if (!result?.success) {
        showToast("error", "Failed to fetch banks.");
      }
    };

    fetchBanks();
  }, []);

  // FUNCTION: Verify Bank Account
  const handleVerifyBankAccount = async () => {
    if (!formData.accNumber || !formData.bankName) {
      showToast("error", "Bank Name and Account Number are required.");
      return;
    }

    const result = await doVerifyBankAccount({
      accNumber: formData.accNumber,
      bankCode: bankCode,
    });

    if (result?.success) {
      // showToast("success", "Bank Account Verified.");
      return true;
    } else {
      showToast("error", errorMessages?.FAILED_BANK_VERIFICATION);
      return false;
    }
  };

  // FUNCTION: Verify Mobile Money
  const handleVerifyMobileMoney = async () => {
    if (!formData.mobileNumber || !formData.mobileMoneyProvider) {
      showToast("error", "Mobile Money Provider and Number are required.");
      return;
    }

    const result = await doVerifyMobileMoney({
      phone: formData.mobileNumber,
      provider: mobileMoneyProviderCode,
      email: formData.email,
    });

    if (result?.success) {
      // showToast("success", "Mobile Money Verified.");
      return true;
    } else {
      showToast("error", errorMessages?.FAILED_MOBILE_MONEY_VERIFICATION);
      return false;
    }
  };

  // FUNCTION: Handle File Upload
  const onFileUpload = (files) => {
    setUploadFiles([...files]);
    setUploadFilesErrors(false);
  };

  const onAddressProofUploade = (files) => {
    setProofAddress([...files]);
    setProofAddressErrors(false);
  };

  const [profilePicture, setProfilePicture] = useState(
    user?.profilePicture ?? null
  );
  const [profilePictureErrors, setProfilePictureErrors] = useState(false);

  // FUNCTION: Handle Profile Image Upload
  const handleProfileImageUpload = (file) => {
    setProfilePicture(file);
    setProfilePictureErrors(false);
  };

  // FUNCTION: Handle Select Bank
  const handleSelectBank = (bank) => {
    if (bank) {
      setBankCode(bank.code);
    } else {
      setBankCode(null);
    }
    handleInputChange("bankName", bank);
  };

  // FUNCTION: Handle Select Mobile Money Provider
  const handleSelectMobileMoneyProvider = (provider) => {
    if (provider) {
      setMobileMoneyProviderCode(provider._id);
    } else {
      setMobileMoneyProviderCode(null);
    }
    handleInputChange("mobileMoneyProvider", provider);
  };

  // console.log("Profile Picture", profilePicture);
  // console.log("id images", uploadFiles);

  // FUNCTION: Validate Serving Areas
  const validateServingAreas = () => {
    let hasErrors = false;
    const updatedErrors = servingAreas.map((city, i) => {
      let cityError = "";
      let subcityError = "";

      if (!city.city) {
        cityError = "City is required";
        hasErrors = true;
      }

      if (city.subcities.length === 0) {
        subcityError = "At least one subcity is required";
        hasErrors = true;
      }

      return {
        ...servingAreasErrors[i],
        city: cityError,
        subcities: subcityError,
      };
    });

    // If there are errors, update state and prevent adding a new city
    if (hasErrors) {
      setServingAreasErrors(updatedErrors);
      setservingAreasTouched((prev) =>
        prev.map((touched, i) => ({
          ...touched,
          city: !servingAreas[i].city,
          subcities: servingAreas[i].subcities.length === 0,
        }))
      );
      return true;
    }

    return false;
  };

  // FUNCTION: Validate Professional Details
  const validateProfessionalDetails = () => {
    let hasErrors = false;

    const updatedErrors = professionalDetails.map((detail, i) => {
      let categoryError = "";
      let categoryNameError = "";
      let skillsError = detail.skills.map((skill, j) => {
        console.log("skill", skill);
        let skillNameError = "";
        let descriptionError = "";

        if (!skill.name) {
          skillNameError = "Skill Name is required";
          hasErrors = true;
        }

        if (!skill.description) {
          descriptionError = "Description is required";
          hasErrors = true;
        }

        return { name: skillNameError, description: descriptionError };
      });

      if (!detail.category) {
        categoryError = "Category is required";
        hasErrors = true;
      }

      return {
        category: categoryError,
        categoryName: categoryNameError,
        skills: skillsError,
      };
    });

    if (hasErrors) {
      setProfessionalDetailsErrors(updatedErrors);
      setProfessionalDetailsTouched((prev) =>
        prev.map((touched, i) => ({
          category: !professionalDetails[i].category,
          categoryName: !professionalDetails[i].categoryName,
          skills: professionalDetails[i].skills.map((skill, j) => ({
            name: !skill.skillName,
            description: !skill.description,
          })),
        }))
      );
    }

    return hasErrors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      // Personal Details
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      idNumber: true,
      aboutMe: true,
      // Password
      password: true,
      confirmPassword: true,
      // Payout Details - Mobile Money
      mobileMoneyProvider: true,
      mobileNumber: true,
      mobileMoneyAccHolderFullName: true,
      // Payout Details - Bank Details
      bankName: true,
      bankAccHolderFullName: true,
      accNumber: true,
      branchName: true,
    });

    const hasProfessionalDetailsErrors = validateProfessionalDetails();
    const hasServingAreasErrors = validateServingAreas();

    const isProofAddressValid = proofAddress.length === 1;
    const isUploadFilesValid =
      uploadFiles.length > 0 && uploadFiles.length <= 2;
    const isProfilePictureValid = profilePicture !== null;

    const isValid =
      validateForm() &&
      !hasServingAreasErrors &&
      !hasProfessionalDetailsErrors &&
      isProofAddressValid &&
      isUploadFilesValid &&
      isProfilePictureValid &&
      paymentType !== null;

    if (!isValid) {
      setProofAddressErrors(!isProofAddressValid);
      setUploadFilesErrors(!isUploadFilesValid);
      setProfilePictureErrors(!isProfilePictureValid);
      if (paymentType === null) setPaymentTypeError(true);

      // Show tooltip with error message
      setTooltipMessage(
        "Required fields missing or have incorrect values. Please check form details again."
      );
      setShowTooltip(true);

      // Hide tooltip after 4 seconds
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      console.log("Validation Failed");
      return;
    } else {
      const cleanedProfessionalDetails = professionalDetails?.map(
        ({ categoryName, skills, ...rest }) => ({
          categoryName,
          skills: skills?.map(({ name, ...skillRest }) => ({
            skillName: name, // Replace 'name' with 'skillName'
            ...skillRest, // Keep other properties - 'description'
          })),
        })
      );
      const cleanedServingAreas = servingAreas?.map(
        ({ city, subcities, ...rest }) => ({
          cityName: city?.name,
          subcities: subcities?.map((subcity) => subcity.name),
        })
      );

      const {
        bankName,
        mobileMoneyProvider,
        accNumber,
        mobileMoneyAccHolderFullName,
        branchName,
        bankAccHolderFullName,
        mobileNumber,
        ...restformData
      } = formData;

      // Create new copies to avoid mutating formData
      let updatedBankName = "";
      let updatedMobileMoneyProvider = "";
      let updatedAccNumber = accNumber;
      let updatedBranchName = branchName;
      let updatedBankAccHolderFullName = bankAccHolderFullName;
      let updatedMobileMoneyAccHolderFullName = mobileMoneyAccHolderFullName;
      let updatedMobileNumber = mobileNumber;

      if (paymentType === paymentTypes.BANK && bankCode) {
        updatedBankName = `${bankName}_${bankCode}`;
        updatedMobileMoneyProvider = ""; // Reset mobile money provider
        updatedMobileMoneyAccHolderFullName = "";
        updatedMobileNumber = "";
      } else {
        updatedMobileMoneyProvider = `${mobileMoneyProvider}_${mobileMoneyProviderCode}`;
        updatedBankName = ""; // Reset bank name
        updatedAccNumber = "";
        updatedBranchName = "";
        updatedBankAccHolderFullName = "";
      }

      const clearFormData = {
        ...restformData,
        bankName: updatedBankName,
        mobileMoneyProvider: updatedMobileMoneyProvider,
        mobileMoneyAccHolderFullName: updatedMobileMoneyAccHolderFullName,
        accNumber: updatedAccNumber,
        branchName: updatedBranchName,
        bankAccHolderFullName: updatedBankAccHolderFullName,
        mobileNumber: updatedMobileNumber,
      };

      if (isAuthenticated && user) {
        delete clearFormData.password;
        delete clearFormData.confirmPassword;
      } else {
        delete clearFormData.confirmPassword;
      }

      // NOTE: Check Verification for Bank Account Number, if payment type is Bank
      if (paymentType === paymentTypes.BANK) {
        const isBankAccountVerified = await handleVerifyBankAccount();
        if (!isBankAccountVerified) {
          return;
        }
      } else {
        // NOTE: Check Verification for Mobile Money, if payment type is Mobile Money
        const isMobileMoneyVerified = await handleVerifyMobileMoney();
        if (!isMobileMoneyVerified) {
          return;
        }
      }

      // NOTE: Upload Address Proof Docs
      const proofAddressUploadResult = await doUploadRefrencesToFirebase(
        proofAddress,
        firebaseUplaodFolders.F_ADDRESS_PROOF,
        maximumImagesUploadingSizes.size_1MB
      );

      if (!proofAddressUploadResult.success) {
        console.error("Upload Error:", proofAddressUploadResult.error);
        showToast("error", "Failed to upload Address Proof.");
        return;
      }

      const uploadedProofAddress = proofAddressUploadResult.uploadedUrls;

      // NOTE: Uplaod Id Images and Profile Picture
      const idImagesUploadResult = await doUploadRefrencesToFirebase(
        uploadFiles,
        firebaseUplaodFolders.F_ID_IMAGES,
        maximumImagesUploadingSizes.size_1MB
      );

      if (!idImagesUploadResult.success) {
        console.error("Upload Error:", idImagesUploadResult.error);
        showToast("error", "Failed to upload ID Images.");
        return;
      }

      const idImages = idImagesUploadResult.uploadedUrls;

      let uploadedProfilePictureUrl = profilePicture || null;
      // first check if profile picture is null or not
      if (profilePicture === null) {
        // if profile picture is null it means user remove the profile picture or not uploaded
        // so we will set the profile picture to null
        setProfilePicture(null);
        uploadedProfilePictureUrl = null;
      } else {
        // Check if profile picture is a new file
        const isNewProfilePicture =
          (profilePicture && profilePicture instanceof File) ||
          (typeof profilePicture === "object" &&
            profilePicture.name &&
            profilePicture.type.startsWith("image/"));

        // Upload profile picture only if it's a new file
        if (isNewProfilePicture) {
          const uploadProfilePictureResult = await doUploadRefrencesToFirebase(
            [profilePicture],
            firebaseUplaodFolders.F_PROFILE_PICTURES
          );

          if (!uploadProfilePictureResult.success) {
            console.error("Upload Error:", uploadProfilePictureResult.error);
            showToast("error", "Failed to upload Profile Picture.");
            return;
          }

          uploadedProfilePictureUrl =
            uploadProfilePictureResult.uploadedUrls[0];
          setProfilePicture(uploadedProfilePictureUrl);
        }
      }

      const data = {
        ...clearFormData,
        idImages,
        professionalDetails: cleanedProfessionalDetails,
        servingAreas: cleanedServingAreas,
        profilePicture: uploadedProfilePictureUrl,
        proofAddress: uploadedProofAddress,
        paymentType,
      };

      const result = await doSignUpAsTasker(data);
      if (result?.success) {
        localStorage.setItem("token", result?.response?.data?.token);

        if (isAuthenticated) {
          // Show only one toast if already authenticated
          showToast(
            "success",
            "Your Registration is Successful, Wait for Admin Approval to work as a Service Provider."
          );
        } else {
          // Show two toasts if registering for the first time
          showToast("success", "Your Registration is Successful.");
          showToast(
            "info",
            "You are automatically registered as a client. To work as a Service Provider, wait for admin approval."
          );
        }
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        showToast("error", result.error?.message || "Failed to register.");
      }
    }
  };

  return (
    <div className="mt-12 px-[1.25rem] md:px-[2.5rem] lg:px-[6.25rem]">
      <h1 className="font-bold font-nonito_sans text-3xl mb-10 sm:text-4xl lg:text-level-1 leading-tight sm:leading-[1.2] lg:leading-[47.5px] text-primary-text text-center tracking-[0.001em]">
        Sign Up
      </h1>
      {/* Form */}
      <div className=" border border-light-blue rounded-[1.25rem] p-4 md:p-9 mb-[5.5rem] bg-[#FFFFFF]">
        {/* Personal Details */}
        <div>
          <FormSectionHeading title="Personal Details" index={1} />
          <div className=" mt-6 flex flex-col gap-[1.875rem]">
            <ProfileImageUploader
              label="Profile Image"
              isRequired={true}
              avatarContainerStyle="w-[119px]"
              imageStyle="w-[119px] h-[115px]"
              labelStyle="font-inter font-medium text-content"
              outerContainerStyle="flex flex-col gap-2"
              onImageUpload={handleProfileImageUpload}
              loading={isUploadRefrencesToFirebaseLoading}
              selectedImage={profilePicture}
              error={profilePictureErrors && "Required profile image"}
            />
            <div className=" grid grid-cols-1 md:grid-cols-2 gap-[1.063rem]">
              <CustomInput
                label="First Name"
                isRequired={true}
                placeholder="Enter First Name"
                labelStyle="text-content"
                type="text"
                name="firstName"
                value={formData.firstName}
                error={touched.firstName && errors.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                onBlur={() => handleBlur("firstName", formData.firstName)}
                onFocus={() => clearError("firstName")}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
              <CustomInput
                label="Last Name"
                isRequired={true}
                placeholder="Enter Last Name"
                labelStyle="text-content"
                type="text"
                name="lastName"
                value={formData.lastName}
                error={touched.lastName && errors.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                onBlur={() => handleBlur("lastName", formData.lastName)}
                onFocus={() => clearError("lastName")}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
            </div>

            {/* Proof Doc for Address */}
            <div className="">
              <ImageUpload
                label="Address Proof"
                uploadText="Jpeg, Jpg, png, pdf are allowed"
                accept={[
                  referencesFormats.JPEG_JPG,
                  referencesFormats.PNG,
                  referencesFormats.PDF,
                ]}
                onFileUpload={onAddressProofUploade}
                uploadFiles={proofAddress}
                isRequired={true}
                labelStyle="text-content"
                numberOfFiles={1}
                isLoading={false}
                error={
                  proofAddressErrors
                    ? "Address Proof is required and upload water bill, electricity bill or any other proof"
                    : ""
                }
              />
            </div>

            <div className=" grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1.063rem]">
              <CustomInput
                label="Email Address"
                isRequired={true}
                placeholder="Enter Email Address"
                labelStyle="text-content"
                type="text"
                name="email"
                value={formData.email}
                error={touched.email && errors.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onBlur={() => handleBlur("email", formData.email)}
                onFocus={() => clearError("email")}
                disabled={isAuthenticated}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
              <CustomInput
                label="Phone Number"
                isRequired={true}
                placeholder="Enter Phone Number"
                labelStyle="text-content"
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                error={touched.phoneNumber && errors.phoneNumber}
                onChange={(e) =>
                  handleInputChange("phoneNumber", e.target.value)
                }
                onBlur={() => handleBlur("phoneNumber", formData.phoneNumber)}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
              <CustomInput
                label="ID Number"
                isRequired={true}
                placeholder="Enter ID Number"
                labelStyle="text-content"
                type="text"
                name="idNumber"
                value={formData.idNumber}
                error={touched.idNumber && errors.idNumber}
                onChange={(e) => handleInputChange("idNumber", e.target.value)}
                onBlur={() => handleBlur("idNumber", formData.idNumber)}
                onFocus={() => clearError("idNumber")}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
            </div>
            <div className="">
              <ImageUpload
                label="ID Images"
                uploadText="Jpeg, Jpg, png are allowed"
                accept={[referencesFormats.JPEG_JPG, referencesFormats.PNG]}
                onFileUpload={onFileUpload}
                uploadFiles={uploadFiles}
                isRequired={true}
                labelStyle="text-content"
                numberOfFiles={2}
                isLoading={false}
                error={uploadFilesErrors ? "Id Images are required" : ""}
              />
            </div>
            <div className="">
              <CustomInput
                label="About Me"
                isRequired={false}
                placeholder="Enter Small Description"
                labelStyle="text-content"
                type="text"
                name="aboutMe"
                value={formData.aboutMe}
                error={touched.aboutMe && errors.aboutMe}
                onChange={(e) => handleInputChange("aboutMe", e.target.value)}
                onBlur={() => handleBlur("aboutMe", formData.aboutMe)}
                className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
              />
            </div>
          </div>
        </div>
        {/* ----- */}

        {/* Password Section */}
        {
          // Note: if user is already authenticated, password section will not be shown
          !isAuthenticated && (
            <div className=" my-10">
              <FormSectionHeading title="Password" index={2} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[1.063rem] mt-6">
                <CustomInput
                  label="Password"
                  isRequired={true}
                  labelStyle="text-content"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  error={touched.password && errors.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  onBlur={() => handleBlur("password", formData.password)}
                  className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  icon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute border-l-2 pl-3 border-[#CFD9E0] right-4 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? (
                        <AiFillEye size={24} className="text-gray-500" />
                      ) : (
                        <AiFillEyeInvisible
                          size={24}
                          className="text-gray-500"
                        />
                      )}
                    </button>
                  }
                />
                <CustomInput
                  label="Re-Enter Password"
                  isRequired={true}
                  labelStyle="text-content"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  error={touched.confirmPassword && errors.confirmPassword}
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  onBlur={() =>
                    handleBlur("confirmPassword", formData.confirmPassword)
                  }
                  className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  icon={
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute border-l-2 pl-3 border-[#CFD9E0] right-4 top-1/2 -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <AiFillEye size={24} className="text-gray-500" />
                      ) : (
                        <AiFillEyeInvisible
                          size={24}
                          className="text-gray-500"
                        />
                      )}
                    </button>
                  }
                />
              </div>
            </div>
          )
        }
        {/* ----- */}

        {/* Professional Details */}
        <ProfessionalDetailsSection
          professionalDetails={professionalDetails}
          setProfessionalDetails={setProfessionalDetails}
          professionalDetailsErrors={professionalDetailsErrors}
          setProfessionalDetailsErrors={setProfessionalDetailsErrors}
          professionalDetailsTouched={professionalDetailsTouched}
          setProfessionalDetailsTouched={setProfessionalDetailsTouched}
        />
        {/* ----- */}

        {/* Serving Areas Section */}
        <ServingAreasSection
          servingAreas={servingAreas}
          setServingAreas={setServingAreas}
          setServingAreasErrors={setServingAreasErrors}
          servingAreasErrors={servingAreasErrors}
          servingAreasTouched={servingAreasTouched}
          setservingAreasTouched={setservingAreasTouched}
        />
        {/* ----- */}

        {/* Payout Section */}
        <div className=" my-10">
          <div className="">
            <FormSectionHeading title="Payout Details" index={5} />
            <div className="flex mt-6 gap-6 flex-wrap md:w-[660px] md:justify-between items-center font-inter text-[14px] font-medium">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentType"
                  value="MOBILE_MONEY"
                  checked={paymentType === paymentTypes.MOBILE_MONEY}
                  onChange={() => {
                    setPaymentTypeError(false);
                    setPaymentType("MOBILE_MONEY");
                    setFormData({
                      ...formData,
                      bankName: "",
                      bankAccHolderFullName: "",
                      accNumber: "",
                      branchName: "",
                    });
                  }}
                  className="hidden"
                />
                <span
                  className={`w-[22px] h-[22px] border border-primary rounded-full flex items-center justify-center ${
                    paymentType === paymentTypes.MOBILE_MONEY
                      ? " bg-transparent"
                      : "bg-white"
                  }`}
                >
                  {paymentType === paymentTypes.MOBILE_MONEY && (
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </span>
                <span className="text-content">Mobile Money</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentType"
                  value="BANK"
                  checked={paymentType === paymentTypes.BANK}
                  onChange={() => {
                    setPaymentTypeError(false);
                    setPaymentType("BANK");
                    setFormData({
                      ...formData,
                      mobileMoneyProvider: "",
                      mobileNumber: "",
                      mobileMoneyAccHolderFullName: "",
                    });
                  }}
                  className="hidden"
                />
                <span
                  className={`w-[22px] h-[22px] border border-primary rounded-full flex items-center justify-center ${
                    paymentType === paymentTypes.BANK
                      ? " bg-transparent"
                      : "bg-white"
                  }`}
                >
                  {paymentType === paymentTypes.BANK && (
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </span>
                <span className="text-content">Bank</span>
              </label>
            </div>
            {
              // Show error if payment type is not selected
              paymentTypeError && (
                <p className="text-light-red text-level-7 pl-4 mt-[12px] font-inter">
                  Payout details are required
                </p>
              )
            }
          </div>

          {/* Payout Details - Modile Money */}
          {paymentType === paymentTypes.MOBILE_MONEY && (
            <div className=" my-6">
              <div className=" mt-6 flex flex-col gap-[0.625rem]">
                <Dropdown
                  options={mobileMoneyProviders}
                  defaultOption={formData.mobileMoneyProvider}
                  onSelect={(selectedOption) =>
                    handleSelectMobileMoneyProvider(selectedOption)
                  }
                  onFocus={() => clearError("mobileMoneyProvider")} // Clear error when user interacts again
                  onBlur={() =>
                    handleBlur(
                      "mobileMoneyProvider",
                      formData.mobileMoneyProvider
                    )
                  }
                  error={
                    touched.mobileMoneyProvider && errors.mobileMoneyProvider
                  }
                  placeholder="Provider"
                  outerContainerStyle="w-full md:w-[16.5rem]"
                  isSearchable={false}
                  label="Mobile Money Provider"
                  displayKey="name"
                  idKey="_id"
                  labelStyle="text-content"
                  buttonClassName="rounded-lg"
                  allowFillColor={false}
                  isRequired={true}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[1.063rem]">
                  <CustomInput
                    label="Mobile Number"
                    isRequired={true}
                    placeholder="Enter Your Mobile Number"
                    labelStyle="text-content"
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    error={touched.mobileNumber && errors.mobileNumber}
                    onChange={(e) =>
                      handleInputChange("mobileNumber", e.target.value)
                    }
                    onBlur={() =>
                      handleBlur("mobileNumber", formData.mobileNumber)
                    }
                    className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  />
                  <CustomInput
                    label="Account Holder's Full Name"
                    isRequired={true}
                    placeholder="Enter Account Holder's Full Name"
                    labelStyle="text-content"
                    type="text"
                    name="mobileMoneyAccHolderFullName"
                    value={formData.mobileMoneyAccHolderFullName}
                    error={
                      touched.mobileMoneyAccHolderFullName &&
                      errors.mobileMoneyAccHolderFullName
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "mobileMoneyAccHolderFullName",
                        e.target.value
                      )
                    }
                    onBlur={() =>
                      handleBlur(
                        "mobileMoneyAccHolderFullName",
                        formData.mobileMoneyAccHolderFullName
                      )
                    }
                    onFocus={() => clearError("mobileMoneyAccHolderFullName")}
                    className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payout Details - Bank Details */}
          {paymentType === paymentTypes.BANK && (
            <div className=" mt-6">
              <div className=" flex flex-col gap-[0.625rem]">
                <div className=" flex flex-wrap gap-[1.0625rem]">
                  <Dropdown
                    placeholder="Bank Name"
                    options={ghanaBanks}
                    defaultOption={formData.bankName}
                    onSelect={(selectedOption) =>
                      handleSelectBank(selectedOption)
                    }
                    onFocus={() => clearError("bankName")} // Clear error when user interacts again
                    onBlur={() => handleBlur("bankName", formData.bankName)}
                    error={touched.bankName && errors.bankName}
                    outerContainerStyle="w-full md:w-[16.5rem]"
                    isSearchable={true}
                    label="Bank Name"
                    displayKey="name"
                    idKey="_id"
                    labelStyle="text-content"
                    buttonClassName="rounded-lg"
                    allowFillColor={false}
                    isRequired={true}
                  />
                  <CustomInput
                    label={"Full Name "}
                    isRequired={true}
                    placeholder={"Enter Your Account Holder's Name"}
                    labelStyle={"text-content"}
                    type={"text"}
                    name={"bankAccHolderFullName"}
                    value={formData.bankAccHolderFullName}
                    error={
                      touched.bankAccHolderFullName &&
                      errors.bankAccHolderFullName
                    }
                    onChange={(e) =>
                      handleInputChange("bankAccHolderFullName", e.target.value)
                    }
                    onBlur={() =>
                      handleBlur(
                        "bankAccHolderFullName",
                        formData.bankAccHolderFullName
                      )
                    }
                    onFocus={() => clearError("bankAccHolderFullName")}
                    outerContainerStyle="flex-1"
                    className={
                      "font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem}"
                    }
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-[1.063rem]">
                  <CustomInput
                    label="Account Number"
                    isRequired={true}
                    placeholder="Enter Your Account Number"
                    labelStyle="text-content"
                    type="text"
                    name="accNumber"
                    value={formData.accNumber}
                    error={touched.accNumber && errors.accNumber}
                    onChange={(e) =>
                      handleInputChange("accNumber", e.target.value)
                    }
                    onBlur={() => handleBlur("accNumber", formData.accNumber)}
                    onFocus={() => clearError("accNumber")}
                    className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  />
                  <CustomInput
                    label="Branch"
                    isRequired={false}
                    placeholder="Enter Branch Name"
                    labelStyle="text-content"
                    type="text"
                    name="branchName"
                    value={formData.branchName}
                    error={touched.branchName && errors.branchName}
                    onChange={(e) =>
                      handleInputChange("branchName", e.target.value)
                    }
                    onBlur={() => handleBlur("branchName", formData.branchName)}
                    onFocus={() => clearError("branchName")}
                    className="font-medium font-inter text-content text-[0.875rem] leading-[1.375rem] w-full rounded-[0.5rem] border border-[#B9C1CA] px-[1rem] py-[0.8125rem] placeholder:text-content placeholder:text-opacity-50 placeholder:text-[0.875rem] placeholder:leading-[1.375rem]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {/* ----- */}

        {/* Action Buttons */}
        <div className="flex flex-col gap-12 mt-10">
          <hr className="w-full border-[#cdd1de]" />
          <div className="flex flex-row gap-1 justify-end">
            <div className="relative">
              {showTooltip && (
                <div className="font-inter absolute bottom-full left-1/2 transform -translate-x-[90px] xs:-translate-x-1/2 mb-2 w-64 py-2 px-3 bg-red-500 text-white text-sm rounded-lg shadow-md transition-opacity duration-300 z-50 text-center">
                  {tooltipMessage}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-8 border-transparent border-t-red-500"></div>
                </div>
              )}
              <CustomButton
                type="submit"
                onClick={handleFormSubmit}
                className="bg-primary text-dark font-inter font-semibold text-sm h-[46px] w-[120px] flex items-center justify-center rounded-xl hover:bg-opacity-90 transition-colors"
                loading={isUploadRefrencesToFirebaseLoading || isSignUpAsTasker}
                buttonText={"Submit"}
                disabled={isUploadRefrencesToFirebaseLoading || isSignUpAsTasker}
                // showTooltip={showTooltip}
                // tooltipMessage={tooltipMessage}
              />
            </div>

            <CustomButton
              buttonText="Cancel"
              onClick={() => navigate(-1)}
              className="text-primary font-inter h-[46px] w-[120px] flex items-center justify-center bg-white font-semibold text-sm hover:text-opacity-80 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* show loader while all the signup data is being processed */}
      {(isUploadRefrencesToFirebaseLoading ||
        isSignUpAsTasker ||
        isVerifyBankAccountLoading ||
        isVerifyMobileMoneyLoading) && (
        <ActionLoader
          message="Please wait until we process your request..."
          loaderColor="#FBC948"
        />
      )}
    </div>
  );
};

export default TaskerSignUp;
